import { Grid } from "./s25.virtual.grid";
import { S25Util } from "../../util/s25-util";
import { computed, NgIterable, Signal, signal, WritableSignal } from "@angular/core";

const FROZEN_CLIP_EPSILON = 1e-9;
/** ANG-7906: Header-unit slack for the 1/100-header rounding in getPositionAfterHidingHeaders. */
const WINDOW_CLIP_EPSILON = 0.01;

export namespace GridUtil {
    export type HeadersMeta<HeaderType extends Grid.SourceHeader = Grid.SourceHeader> = ReturnType<
        typeof GridUtil.getHeadersMetadata<HeaderType>
    >;
    export function getHeadersMetadata<HeaderType extends Grid.SourceHeader>(headers: Signal<readonly HeaderType[]>) {
        const flatMeta = computed(() => GridUtil.getHeaderData(headers()));
        return {
            depth: computed(() => headersDepth(headers())),
            flatMeta,
            count: computed(() => flatMeta()?.length),
            visibleCount: computed(() => {
                const meta = flatMeta();
                return !meta ? 0 : S25Util.array.count(meta, (header) => +!header.hidden);
            }),
        };
    }

    export function headersDepth(headers: readonly Grid.SourceHeader[]): number {
        return Math.max(
            1,
            ...headers
                .filter((header) => !header.hidden?.())
                .map((header) => (header.subHeaders?.length ? 1 + headersDepth(header.subHeaders) : 1)),
        );
    }

    /**
     * Whether a clipped position still occupies space. Hiding the row or column an entry sits in collapses it to
     * zero size, so this is what decides whether the entry is worth rendering at all.
     */
    export function isOnGrid(position: Grid.Rect): boolean {
        return position.width > 0 && position.height > 0;
    }

    export function clipPosition(
        position: Grid.Rect,
        columnHeadersMeta: HeadersMeta<any>,
        rowHeadersMeta: HeadersMeta<any>,
    ): Grid.Rect {
        const horizontal = clipPosition1D(position.left, position.width, columnHeadersMeta);
        const vertical = clipPosition1D(position.top, position.height, rowHeadersMeta);
        return {
            left: horizontal.start,
            width: horizontal.size,
            top: vertical.start,
            height: vertical.size,
        };
    }

    function clipPosition1D(start: number, size: number, headersMeta: HeadersMeta<any>) {
        const headerData = headersMeta.flatMeta();
        if (!headerData.length) return { start, size };
        const truncatedStart = GridUtil.getPositionAfterHidingHeaders(start, headersMeta);
        let right = GridUtil.truncateItemByHeader(start, start + size, headerData);
        right = GridUtil.getPositionAfterHidingHeaders(right, headersMeta);
        return { start: truncatedStart, size: right - truncatedStart };
    }

    /**
     * ANG-7906: The tile is the clipped rect. This maps the unclipped item onto that tile so a
     * consumer lays the whole item out and the tile shows only the visible part.
     *
     * A hidden header in the middle of an item collapses into one tile; the window lines up the
     * first visible segment and treats the rest as clipped off the far side.
     */
    export function getItemWindow(
        position: Grid.Rect,
        clippedPosition: Grid.Rect,
        columnHeadersMeta: HeadersMeta<any>,
        rowHeadersMeta: HeadersMeta<any>,
    ): Grid.ItemWindow {
        const horizontal = itemWindowAxis(
            position.left,
            position.width,
            clippedPosition.left,
            clippedPosition.width,
            columnHeadersMeta,
        );
        const vertical = itemWindowAxis(
            position.top,
            position.height,
            clippedPosition.top,
            clippedPosition.height,
            rowHeadersMeta,
        );
        return {
            full: {
                left: horizontal.start,
                width: horizontal.size,
                top: vertical.start,
                height: vertical.size,
            },
            clipped: {
                left: horizontal.clippedBefore,
                right: horizontal.clippedAfter,
                top: vertical.clippedBefore,
                bottom: vertical.clippedAfter,
            },
        };
    }

    function itemWindowAxis(
        fullStart: number,
        fullSize: number,
        clippedStart: number,
        clippedSize: number,
        headersMeta: HeadersMeta<any>,
    ) {
        const identity = { start: 0, size: 100, clippedBefore: false, clippedAfter: false };
        const total = headersMeta.flatMeta().length;
        const visible = headersMeta.visibleCount();
        if (!total || !visible || clippedSize <= 0) return identity;

        const clippedUnits = (clippedSize / 100) * visible;
        if (clippedUnits <= 0) return identity;

        const fullUnits = (fullSize / 100) * total;
        const hiddenBeforeUnits = Math.max(
            0,
            ((GridUtil.getPositionBeforeHidingHeaders(clippedStart, headersMeta) - fullStart) / 100) * total,
        );
        const hiddenAfterUnits = fullUnits - hiddenBeforeUnits - clippedUnits;
        const clippedBefore = hiddenBeforeUnits > WINDOW_CLIP_EPSILON;
        const clippedAfter = hiddenAfterUnits > WINDOW_CLIP_EPSILON;
        if (!clippedBefore && !clippedAfter) return identity;

        return {
            start: (-hiddenBeforeUnits / clippedUnits) * 100,
            size: (fullUnits / clippedUnits) * 100,
            clippedBefore,
            clippedAfter,
        };
    }

    export function getPositionAfterHidingHeaders(percent: number, headersMeta: HeadersMeta<any>) {
        const headerData = headersMeta.flatMeta();
        const visibleHeaders = headersMeta.visibleCount();
        const headers = headerData.length;
        if (!headers || !visibleHeaders) return percent;
        let pos = (percent / 100) * headers; // Convert to header units
        pos = Math.round(pos * headers * 100) / (headers * 100); // Align to a 100th of a header
        let newPos = pos;
        newPos -= headerData[Math.floor(pos) - 1]?.hiddenSum || 0; // Subtract preceding hidden headers
        if (headerData[Math.floor(pos)]?.hidden) newPos = Math.floor(newPos); // Pos itself is hidden, align to header
        newPos = Math.round(newPos * headers * 100) / (headers * 100); // Align to a 100th of a header
        return (newPos / visibleHeaders) * 100; // Convert back to percent
    }

    export function getPositionBeforeHidingHeaders(percent: number, headersMeta: HeadersMeta<any>): number {
        const headerData = headersMeta.flatMeta();
        const visibleHeaders = headersMeta.visibleCount();
        if (!headerData.length || !visibleHeaders) return percent;
        // Past the displayed headers, continue one header at a time from the converted edge. The anchor
        // already includes every hidden header before or after the displayed run.
        if (percent < 0 || percent > 100) {
            const anchor = percent < 0 ? 0 : 100;
            const anchorFull = getPositionBeforeHidingHeaders(anchor, headersMeta);
            return anchorFull + (percent - anchor) * (visibleHeaders / headerData.length);
        }
        let pos = (percent / 100) * visibleHeaders; // Convert to header units
        pos = Math.round(pos * visibleHeaders * 100) / (visibleHeaders * 100); // Align to a 100th of a header
        const visibleHeaderIndex = Math.floor(pos);
        let headerIndex: number, newPos: number;
        if (visibleHeaderIndex >= visibleHeaders) {
            // We're at the end of the last visible header
            headerIndex = S25Util.array.findLastIndex(headerData, (header) => !header.hidden);
            newPos = headerIndex + 1;
        } else {
            headerIndex = headerData.findIndex((header, i) => i - header.hiddenSum === visibleHeaderIndex);
            newPos = headerIndex + (pos % 1);
        }
        return (newPos / headerData.length) * 100; // Convert back to percent
    }

    export function truncateItemByHeader<HeaderType extends Grid.SourceHeader>(
        start: number,
        end: number,
        headerData: Grid.HeaderMeta<HeaderType>[],
    ) {
        // If a header is marked with "truncateOverflow" we need to truncate any item which overflows the header
        // If two successive headers have different truncateRef, truncate and return
        if (!headerData.length) return end;
        let startCol = Math.floor((S25Util.clamp(start, 0, 100) / 100) * headerData.length);
        startCol = S25Util.clamp(startCol, 0, headerData.length - 1);
        const endCol = (S25Util.clamp(end, 0, 100) / 100) * headerData.length;
        let truncateRef = headerData[startCol]?.truncateRef;
        for (let i = startCol; i < endCol; i++) {
            if (headerData[i].truncateRef !== truncateRef) {
                // Truncate to start of header
                return (i / headerData.length) * 100;
            }
            truncateRef = headerData[i].truncateRef;
        }

        return end;
    }

    export function isItemTruncated<HeaderType extends Grid.SourceHeader>(
        position: Grid.Rect,
        columnData: Grid.HeaderMeta<HeaderType>[],
        rowData: Grid.HeaderMeta<HeaderType>[],
    ) {
        const [firstCol, lastCol] = getGridUnits(position.left, position.width, columnData.length);
        let truncateRef = columnData[firstCol]?.truncateRef;
        for (let i = firstCol; i <= lastCol; i++) {
            if (columnData[i]?.truncateRef !== truncateRef) return true; // Truncated by "truncateOverflow" on header
            if (columnData[i]?.hidden) return true; // Truncated by hidden header
        }

        const [firstRow, lastRow] = getGridUnits(position.top, position.height, rowData.length);
        truncateRef = rowData[firstRow]?.truncateRef;
        for (let i = firstRow; i <= lastRow; i++) {
            if (rowData[i]?.truncateRef !== truncateRef) return true; // Truncated by "truncateOverflow" on header
            if (rowData[i]?.hidden) return true; // Truncated by hidden header
        }

        return false;
    }

    export function getGridUnits(offset: number, size: number, count: number) {
        // e is to avoid counting an item as being in a row/column if it's right on the edge
        const e = 1.0e-12;
        const first = Math.floor((S25Util.clamp(offset + e, 0, 100) / 100) * count);
        const last = Math.floor((S25Util.clamp(offset + size - e, 0, 100) / 100) * count);
        return [first, last];
    }

    export function getHeaderData<HeaderType extends Grid.SourceHeader>(
        headers: readonly HeaderType[],
    ): Grid.HeaderMeta<HeaderType>[] {
        const data = getHeaderDataDFS(headers);
        const lastVisibleIndex = S25Util.array.findLastIndex(data, (metadata) => !metadata.hidden);
        return data.map((metadata, index) => ({ ...metadata, last: index === lastVisibleIndex }));
    }

    function getHeaderDataDFS<HeaderType extends Grid.SourceHeader>(
        headers: readonly HeaderType[],
        hiddenParent = false,
        truncateRef: Grid.SourceHeader | undefined = undefined,
        data: Grid.HeaderMeta<HeaderType>[] = [],
    ): Grid.HeaderMeta<HeaderType>[] {
        for (const header of headers) {
            const hidden = hiddenParent || !!header.hidden?.();
            if (header.subHeaders) {
                getHeaderDataDFS(header.subHeaders, hidden, header.truncateOverflow ? header : truncateRef, data);
            } else {
                const hiddenSum = (data.at(-1)?.hiddenSum || 0) + +hidden;
                data.push({
                    hidden,
                    hiddenSum,
                    truncateRef: header.truncateOverflow ? header : truncateRef,
                    header,
                    last: false,
                });
            }
        }
        return data;
    }

    export function positionToHeaders<HeaderType extends Grid.SourceHeader>(
        offset: number,
        size: number,
        snap: number,
        headerData: Grid.HeaderMeta<HeaderType>[],
    ) {
        const err = snap / 100;
        const start = Math.round(((offset / 100) * headerData.length) / err) * err;
        const end = Math.round((((offset + size) / 100) * headerData.length) / err) * err;
        const headers: HeaderType[] = [];
        for (let i = Math.floor(start); i < end; i++) {
            headers.push(headerData[i].header);
        }

        return headers;
    }

    export function getCreatePosition<
        ColumnType extends Grid.SourceHeader,
        RowType extends Grid.SourceHeader,
    >(options: {
        creating: Grid.Creating;
        gridArea: HTMLElement;
        columnMeta: HeadersMeta<ColumnType>;
        rowMeta: HeadersMeta<RowType>;
    }): Grid.Rect | undefined {
        const { creating, gridArea, columnMeta, rowMeta } = options;
        if (!creating) return;

        const { start, end } = creating;
        const columnVisibleCount = columnMeta.visibleCount();
        const rowVisibleCount = rowMeta.visibleCount();
        const rowHeight = gridArea.offsetHeight / rowVisibleCount;
        const columnWidth = gridArea.offsetWidth / columnVisibleCount;

        // Account for flip when dragging forwards vs backwards
        const originX = end.x < start.x ? start.x + columnWidth : start.x;
        const originY = end.y < start.y ? start.y + rowHeight : start.y;

        // Position in DOM
        const visualTop = (Math.min(originY, end.y) / gridArea.offsetHeight) * 100;
        const visualBottom = (Math.max(originY, end.y) / gridArea.offsetHeight) * 100;
        const visualLeft = (Math.min(originX, end.x) / gridArea.offsetWidth) * 100;
        const visualRight = (Math.max(originX, end.x) / gridArea.offsetWidth) * 100;

        // TODO: If there is an item partially covering the target area, show create button only in the available space
        // For example in separated mode of avail weekly. Note: there needs to be a minimum space requirement
        // Fall back to showing it over the whole area
        // When creation is initialized, resize the item to cover the whole target area, overlapping other items

        // Position after accounting for hidden rows/columns
        const top = GridUtil.getPositionBeforeHidingHeaders(visualTop, rowMeta);
        const bottom = GridUtil.getPositionBeforeHidingHeaders(visualBottom, rowMeta);
        const left = GridUtil.getPositionBeforeHidingHeaders(visualLeft, columnMeta);
        const right = GridUtil.getPositionBeforeHidingHeaders(visualRight, columnMeta);

        return { top, height: bottom - top, left, width: right - left };
    }

    export function initializeHeader<HeaderType extends Grid.SourceHeader>(
        header: Grid.UnInitializedHeader<HeaderType>,
        metadata: HeadersMeta<HeaderType>,
        parent?: Grid.Header<HeaderType>,
    ): Grid.Header<HeaderType> {
        const asInitializedHeader = header as unknown as Grid.Header<HeaderType>;
        if (!!header._virtualGrid?.initialized) return asInitializedHeader;
        header._virtualGrid = {
            ...(header._virtualGrid ?? {}),
            initialized: true,
            parent,
            isLast: computed(() => metadata.flatMeta().some((entry) => entry.header === header && entry.last)),
        };
        for (const subHeader of header.subHeaders ?? []) {
            initializeHeader(subHeader as Grid.UnInitializedHeader<HeaderType>, metadata, asInitializedHeader);
        }
        return asInitializedHeader;
    }

    export function initializeItem<ItemType extends Grid.SourceItem, ProjectionData extends Grid.CustomData>(options: {
        item: Grid.UnInitializedItem<ItemType>;
        findOverlap: Signal<boolean>;
        overlap: Signal<Grid.OverlapBundle<ItemType, ProjectionData>>;
        itemInitialized?: (item: Grid.Item<ItemType>) => void;
    }): Grid.Item<ItemType> {
        const { item, findOverlap, overlap, itemInitialized } = options;
        const asInitializedItem = item as unknown as Grid.Item<ItemType>;
        if (!!item._virtualGrid?.initialized) return asInitializedItem;

        const dragOffsetSource = signal(signal<Grid.Coord | undefined>(undefined));
        const candidatePosition = signal<Grid.PositionOf<ItemType> | undefined>(undefined);

        item.virtualGrid = {
            ...(item.virtualGrid ?? {}),
            overlappingItems: computed(() => (!findOverlap() ? [] : (overlap()?.byItemId.get(item.id) ?? []))),
            candidate: {
                offset: computed(() => dragOffsetSource()()),
                position: candidatePosition.asReadonly(),
            },
        };

        item._virtualGrid = {
            ...(item._virtualGrid ?? {}),
            initialized: true,
            isBeingDragged: signal(false),
            dragOffsetSource,
            candidatePosition,
            moving: signal(undefined),
            broughtToFront: signal(false),
        };

        itemInitialized?.(asInitializedItem);
        return asInitializedItem;
    }

    // Creates a map from column X and row Y to a list of items that are present in that slice of the grid
    export function createItemIndex<Item>(
        items: NgIterable<Item>,
        rowCount: number,
        colCount: number,
        read: (item: Item) => Grid.Rect = (item) => (item as Grid.Positioned).position(),
    ): Grid.ItemIndex<Item> {
        const byRow = new Map<number, Map<number, Item[]>>();
        for (const item of items) {
            const pos = read(item);
            const [firstRow, lastRow] = getGridUnits(pos.top, pos.height, rowCount);
            for (let r = firstRow; r <= lastRow; r++) {
                const byCol = byRow.get(r) || new Map<number, Item[]>();
                byRow.set(r, byCol);

                const [firstCol, lastCol] = getGridUnits(pos.left, pos.width, colCount);
                for (let c = firstCol; c <= lastCol; c++) {
                    const colArr = byCol.get(c) || [];
                    byCol.set(c, colArr);

                    colArr.push(item);
                }
            }
        }

        return byRow;
    }

    export function getEntriesInPosition<Entry>(
        position: Grid.Rect,
        itemIndex: Grid.ItemIndex<Entry>,
        columnCount: number,
        rowCount: number,
        readPosition: (entry: Entry) => Grid.Rect = (entry) => (entry as Grid.Positioned).position(),
    ): Entry[] {
        const [firstCol, lastCol] = getGridUnits(position.left, position.width, columnCount);
        const [firstRow, lastRow] = getGridUnits(position.top, position.height, rowCount);
        const nearby = collectItemsInRegion(itemIndex, firstRow, lastRow, firstCol, lastCol);
        return nearby.filter((entry) => GridUtil.doBoxesOverlap(position, readPosition(entry)));
    }

    export function includeOnMinimap(entry: Grid.RenderEntry): boolean {
        return !(entry.type === "decoration" && entry.data?.type === "aggregate-overlap");
    }

    export function collectItemsInRegion<Item>(
        itemIndex: Grid.ItemIndex<Item>,
        firstRow: number,
        lastRow: number,
        firstCol: number,
        lastCol: number,
    ): Item[] {
        const seen = new Set<Item>();
        for (let r = firstRow; r <= lastRow; r++) {
            for (let c = firstCol; c <= lastCol; c++) {
                for (const item of itemIndex.get(r)?.get(c) || []) {
                    if (seen.has(item)) continue;
                    seen.add(item);
                }
            }
        }
        return Array.from(seen);
    }

    export function getItemsInRegion<Item>(
        itemIndex: Grid.ItemIndex<Item>,
        firstRow: number,
        lastRow: number,
        firstCol: number,
        lastCol: number,
        getKey?: (item: Item) => string | number,
        readPosition: (item: Item) => Grid.Rect = (item) => (item as Grid.Positioned).position(),
    ): Item[] {
        // Viewport queries sort because items may stretch beyond the specified region.
        return GridUtil.sortItems(
            collectItemsInRegion(itemIndex, firstRow, lastRow, firstCol, lastCol),
            getKey,
            readPosition,
        );
    }

    // Maps each projection to the projections of other source items whose boxes it overlaps.
    // Projections of the same source item never overlap each other, so a shadow only appears
    // here when that specific shadow box collides with another item.
    export function overlappingProjectionsByKey<
        ItemType extends Grid.SourceItem,
        ProjectionData extends Grid.CustomData,
    >(
        projections: readonly Grid.ItemProjection<ItemType, ProjectionData>[],
        columnCount: number,
        rowCount: number,
    ): Grid.OverlapNeighbors<ItemType, ProjectionData> {
        const snapshots = projections.map((projection) => {
            const { left, top, width, height } = projection.clippedPosition();
            return { projection, position: { left, top, width, height } };
        });
        const readSnapshotPosition = (snapshot: (typeof snapshots)[number]) => snapshot.position;
        const projectionIndex = GridUtil.createItemIndex(snapshots, rowCount, columnCount, readSnapshotPosition);
        const neighbors = new Map<Grid.RenderEntry["key"], Grid.ItemProjection<ItemType, ProjectionData>[]>();
        for (const snapshot of snapshots) {
            const nearby = GridUtil.getEntriesInPosition(
                snapshot.position,
                projectionIndex,
                columnCount,
                rowCount,
                readSnapshotPosition,
            );
            const overlapping = nearby
                .map((otherSnapshot) => otherSnapshot.projection)
                .filter((otherProjection) => otherProjection.source.id !== snapshot.projection.source.id);
            if (overlapping.length) neighbors.set(snapshot.projection.key, overlapping);
        }
        return neighbors;
    }

    export function findOverlapSegments<ItemType extends Grid.SourceItem, ProjectionData extends Grid.CustomData>(
        projections: readonly Grid.ItemProjection<ItemType, ProjectionData>[],
        neighbors: Grid.OverlapNeighbors<ItemType, ProjectionData>,
    ): Grid.OverlapSegment<ItemType, ProjectionData>[] {
        const seen = new Set<Grid.RenderEntry["key"]>();
        const segments: Grid.OverlapSegment<ItemType, ProjectionData>[] = [];
        for (const projection of projections) {
            if (seen.has(projection.key)) continue;
            const overlapping = neighbors.get(projection.key) ?? [];
            if (!overlapping.length) continue;

            const cluster: Grid.ItemProjection<ItemType, ProjectionData>[] = [];
            const queue = [projection];
            seen.add(projection.key);
            while (queue.length) {
                const current = queue.pop()!;
                cluster.push(current);
                for (const next of neighbors.get(current.key) ?? []) {
                    if (seen.has(next.key)) continue;
                    seen.add(next.key);
                    queue.push(next);
                }
            }

            const itemsById = new Map<Grid.SourceItem["id"], Grid.Item<ItemType>>();
            let left = Infinity;
            let right = -Infinity;
            let top = Infinity;
            let bottom = -Infinity;
            for (const member of cluster) {
                itemsById.set(member.source.id, member.source);
                const box = member.position();
                left = Math.min(left, box.left);
                right = Math.max(right, box.left + box.width);
                top = Math.min(top, box.top);
                bottom = Math.max(bottom, box.top + box.height);
            }
            if (itemsById.size < 2) continue;
            segments.push({
                items: [...itemsById.values()],
                projections: cluster,
                position: {
                    left,
                    width: right - left,
                    top,
                    height: bottom - top,
                },
            });
        }

        return segments;
    }

    export function overlappingItemsByProjectionKey<
        ItemType extends Grid.SourceItem,
        ProjectionData extends Grid.CustomData,
    >(
        neighbors: Grid.OverlapNeighbors<ItemType, ProjectionData>,
    ): ReadonlyMap<Grid.RenderEntry["key"], Grid.Item<ItemType>[]> {
        const overlapping = new Map<Grid.RenderEntry["key"], Grid.Item<ItemType>[]>();
        for (const [projectionKey, projections] of neighbors) {
            const itemsById = new Map<Grid.SourceItem["id"], Grid.Item<ItemType>>();
            for (const projection of projections) itemsById.set(projection.source.id, projection.source);
            overlapping.set(projectionKey, [...itemsById.values()]);
        }
        return overlapping;
    }

    export function overlappingSourcesByItemId<
        ItemType extends Grid.SourceItem,
        ProjectionData extends Grid.CustomData,
    >(
        projections: readonly Grid.ItemProjection<ItemType, ProjectionData>[],
        neighbors: Grid.OverlapNeighbors<ItemType, ProjectionData>,
    ): ReadonlyMap<Grid.SourceItem["id"], Grid.Item<ItemType>[]> {
        const overlapping = new Map<Grid.SourceItem["id"], Map<Grid.SourceItem["id"], Grid.Item<ItemType>>>();
        for (const projection of projections) {
            const overlappingProjections = neighbors.get(projection.key);
            if (!overlappingProjections?.length) continue;
            const others = overlapping.get(projection.source.id) ?? new Map();
            for (const other of overlappingProjections) others.set(other.source.id, other.source);
            overlapping.set(projection.source.id, others);
        }
        return new Map([...overlapping].map(([itemId, others]) => [itemId, [...others.values()]]));
    }

    // Sort by top then left so tabbing goes row by row, then by a stable key
    export function sortItems<ItemType>(
        items: readonly ItemType[],
        getKey?: (item: ItemType) => string | number,
        readPosition: (item: ItemType) => Grid.Rect = (item) => (item as Grid.Positioned).position(),
    ): ItemType[] {
        return [...items].sort((leftItem, rightItem) => {
            const leftPosition = readPosition(leftItem);
            const rightPosition = readPosition(rightItem);
            if (!leftPosition || !rightPosition) return 0;
            if (leftPosition.top !== rightPosition.top) return leftPosition.top - rightPosition.top;
            if (leftPosition.left !== rightPosition.left) return leftPosition.left - rightPosition.left;
            if (!getKey) return 0;
            const leftKey = String(getKey(leftItem));
            const rightKey = String(getKey(rightItem));
            if (leftKey === rightKey) return 0;
            return leftKey < rightKey ? -1 : 1;
        });
    }

    export function snap(
        position: number, // Percent of total grid
        step: number, // Fraction of a header
        offset: number, // Fraction of a header
        headerCount: number,
    ) {
        const headersUnits = (position / 100) * headerCount - offset; // Convert to header units
        const snapped = Math.round(headersUnits / step) * step; // Snap!
        return ((snapped + offset) / headerCount) * 100; // Convert back to percent
    }

    export function applyPosition(item: { position: WritableSignal<Grid.Rect> }, position: Partial<Grid.Rect>): void {
        item.position.update((current) => ({
            left: position.left ?? current.left,
            top: position.top ?? current.top,
            width: position.width ?? current.width,
            height: position.height ?? current.height,
        }));
    }

    const positionEpsilon = 1.0e-9;
    function coordsEqual(left: number, right: number): boolean {
        return Math.abs(left - right) < positionEpsilon;
    }

    export function positionsEqual(left: Grid.Rect, right: Grid.Rect): boolean {
        return (
            coordsEqual(left.left, right.left) &&
            coordsEqual(left.top, right.top) &&
            coordsEqual(left.width, right.width) &&
            coordsEqual(left.height, right.height)
        );
    }

    export function doBoxesOverlap(a: Grid.Rect, b: Grid.Rect): boolean {
        const overlapEpsilon = 1.0e-12;
        const horizontal = a.left + overlapEpsilon < b.left + b.width && a.left + a.width - overlapEpsilon > b.left;
        if (!horizontal) return false;
        return a.top + overlapEpsilon < b.top + b.height && a.top + a.height - overlapEpsilon > b.top;
    }

    export function emptyOverlapBundle<
        ItemType extends Grid.SourceItem,
        ProjectionData extends Grid.CustomData,
    >(): Grid.OverlapBundle<ItemType, ProjectionData> {
        return { segments: [], byProjectionKey: new Map(), byItemId: new Map() };
    }

    /**
     * Clip a visible-grid rect to the frozen row band and remap Y into that band.
     * A sliver at the boundary (within 1e-9 percent) is not a tile.
     */
    export function clipToFrozenRows(
        position: Grid.Rect,
        frozenRows: number,
        visibleRowCount: number,
    ): Grid.Rect | undefined {
        if (frozenRows <= 0 || visibleRowCount <= 0) return undefined;
        const bandEnd = (frozenRows / visibleRowCount) * 100;
        const clippedStart = Math.max(position.top, 0);
        const clippedEnd = Math.min(position.top + position.height, bandEnd);
        if (clippedEnd - clippedStart <= FROZEN_CLIP_EPSILON) return undefined;
        return {
            left: position.left,
            width: position.width,
            top: (clippedStart / bandEnd) * 100,
            height: ((clippedEnd - clippedStart) / bandEnd) * 100,
        };
    }

    /**
     * FLIP: the element is already laid out at `target`, so it is pushed back to `origin` and that
     * offset is animated away. Unlike a CSS transition on the geometry custom properties, animating
     * a transform does not care that the committed geometry is already the destination.
     *
     * Slides a tile from the geometry it used to occupy to the one it occupies now.
     *
     * Both rects are percentages of `reference`, the tile's containing block, so the offsets come
     * from geometry the caller already holds rather than from measuring the tile. Measuring the tile
     * cannot be trusted here: its own rect includes any in-flight move transform, and it has to be
     * read back in the same task as the style write that moves it.
     */
    export function flipElement(
        element: HTMLElement,
        origin: Grid.Rect,
        target: Grid.Rect,
        reference: { width: number; height: number },
        durationMs: number,
    ): Animation | undefined {
        const subPixel = 1;
        // A tile in a collapsed container was not on screen to fly from
        if (!reference.width || !reference.height || typeof element.animate !== "function") return;

        const toPixelsX = (percent: number) => (percent / 100) * reference.width;
        const toPixelsY = (percent: number) => (percent / 100) * reference.height;
        const offsetX = toPixelsX(origin.left - target.left);
        const offsetY = toPixelsY(origin.top - target.top);
        const originWidth = toPixelsX(origin.width);
        const targetWidth = toPixelsX(target.width);
        const resized = Math.abs(originWidth - targetWidth) >= subPixel;
        if (Math.abs(offsetX) < subPixel && Math.abs(offsetY) < subPixel && !resized) return;

        const from: Keyframe = { transform: `translate(${offsetX}px, ${offsetY}px)` };
        const to: Keyframe = { transform: "none" };
        if (resized) {
            from.width = `${originWidth}px`;
            to.width = `${targetWidth}px`;
        }
        return element.animate([from, to], { duration: durationMs, easing: "ease-out" });
    }
}
